import { View, Text, styled, ScrollView } from 'tamagui'
import { UserPlus } from '@tamagui/lucide-icons'
import { useAppStore } from '@/store'
import type { FriendsTab } from '@/store'
import { friendships } from '@/mocks/dms'
import { users } from '@/mocks'
import { FriendRow } from './FriendRow'

const Container = styled(View, {
  flex: 1,
  flexDirection: 'column',
  backgroundColor: '$surface',
})

const Header = styled(View, {
  height: 48,
  paddingHorizontal: 16,
  flexDirection: 'row',
  alignItems: 'center',
  gap: 16,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const HeaderTitle = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 15,
  color: '$surfaceForeground',
  marginRight: 8,
})

const Tab = styled(View, {
  paddingHorizontal: 10,
  paddingVertical: 6,
  borderRadius: '$borderRadiusMd',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  variants: {
    active: {
      true: {
        backgroundColor: '$sidebarActive',
      },
    },
  } as const,
})

const TabText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  fontWeight: '500',
})

const AddFriendButton = styled(View, {
  marginLeft: 'auto',
  height: 32,
  paddingHorizontal: 12,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$accent',
  cursor: 'pointer',
  flexDirection: 'row',
  gap: 6,
  hoverStyle: { opacity: 0.9 },
})

const Body = styled(View, { padding: 16, gap: 2, maxWidth: 800 })

const EmptyState = styled(View, {
  padding: 48,
  alignItems: 'center',
})

const tabs: { id: FriendsTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'online', label: 'Online' },
  { id: 'pending', label: 'Pending' },
  { id: 'blocked', label: 'Blocked' },
]

export function FriendsList() {
  const { friendsTab, setFriendsTab, openAddFriendModal } = useAppStore()

  const filteredFriends = friendships.filter((f) => {
    const targetId = f.userId === 'u1' ? f.targetId : f.userId
    const user = users[targetId]
    if (!user) return false

    switch (friendsTab) {
      case 'all':
        return f.status === 'accepted'
      case 'online':
        return f.status === 'accepted' && user.status !== 'offline'
      case 'pending':
        return f.status === 'pending_incoming' || f.status === 'pending_outgoing'
      case 'blocked':
        return f.status === 'blocked'
      default:
        return false
    }
  })

  return (
    <Container>
      <Header>
        <HeaderTitle>Friends</HeaderTitle>
        {tabs.map((tab) => (
          <Tab key={tab.id} active={friendsTab === tab.id} onPress={() => setFriendsTab(tab.id)}>
            <TabText color={friendsTab === tab.id ? '$surfaceForeground' : '$sidebarMuted'}>
              {tab.label}
              {tab.id === 'pending' && friendships.filter(f => f.status === 'pending_incoming').length > 0 && (
                <Text color="$error" fontSize={11}> •</Text>
              )}
            </TabText>
          </Tab>
        ))}
        <AddFriendButton onPress={openAddFriendModal}>
          <UserPlus size={14} color="$surface" />
          <Text fontFamily="$fontBody" fontWeight="600" fontSize={12} color="$surface">Add Friend</Text>
        </AddFriendButton>
      </Header>

      <ScrollView flex={1}>
        <Body>
          {filteredFriends.length === 0 ? (
            <EmptyState>
              <Text fontFamily="$fontBody" fontSize={14} color="$sidebarMuted">
                {friendsTab === 'pending' ? 'No pending requests' :
                 friendsTab === 'blocked' ? 'No blocked users' :
                 friendsTab === 'online' ? 'No friends online' :
                 'No friends yet. Add someone!'}
              </Text>
            </EmptyState>
          ) : (
            filteredFriends.map((f) => {
              const targetId = f.userId === 'u1' ? f.targetId : f.userId
              const user = users[targetId]
              if (!user) return null
              return <FriendRow key={f.id} friendship={f} user={user} />
            })
          )}
        </Body>
      </ScrollView>
    </Container>
  )
}
